import React, { CSSProperties, memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, frames, glyphs, icons } from "../../Styles";
import { NewSelect, NewSelectList, PageHeader, Overlay } from "../../Components";
import { ButtonTypes, ListStyle_Types, NewFieldStyles, SelectFieldOption_Values } from "../../Definitions";
import { useSectionOptions, useSystemOptions } from "../../Components/_Components";
import { Pages } from "../../Navigation/root";
import { useAppDispatch, useAppSelector } from "../../Store";
import { AnimalInfo, Graph_QL, MaintainAnimal, useAnimal, useSection, useSkipFeedTime } from "../../Services";
import FvLocationFeeder from "../../Components/FocusViewComponent/fv_locationFeeder";
import { addActiveLocations, clearActiveLocations, resetActiveLocations, selectAllActiveSections } from "../../Store/activeStateHandlers/activeSectionSlice";
import { addActiveAnimals, clearActiveAnimals, resetActiveAnimals } from "../../Store/activeStateHandlers/activeAnimalSlice";
import { isEqual } from "lodash";
import { selectByLocations } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { Location } from "../../Services";
import NewButton, { NewButtonList } from "../../Components/BaseComponents/newButton";
import { createPortal } from "react-dom";


// #region | Pen List
const PenList = memo(({ locations, onClick }: { locations: Location[] | undefined, onClick: (id: number) => void }) => {
  const { t } = useTranslation();
  const fontStyle: CSSProperties = { ...fonts.paragraph, ...fonts.fontSize3, color: colors.greyDarkPrimary }; 
  const flexStyle: CSSProperties = {  width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", flexShrink: "0", gap: "5px" };

  return(
    <div style={{ height: "100%", width: "100%" }}>
      {locations?.map((loc, index) => (
        <div key={index} onClick={() => onClick(loc.location_id)} style={{ width: "100%", padding: "10px 20px", boxSizing: "border-box", color: colors.greyDarkPrimary, backgroundColor: index % 2 ? colors.greyLightPrimary : colors.greyLightSecondary,  display: "grid", gridTemplateRows: "1fr", gridTemplateColumns: "100px 1fr", alignItems: "start", gap: "20px" }}>
          {/* PEN NAME */}
          <div style={{ ...flexStyle, gridArea: "1/1/2/2", height: "30px" }}>
            <glyphs.ArrowSmooth fillColor={colors.greyDarkPrimary} size={"25px"} />
            <p style={{ ...fontStyle }}>{`${t("focus_viewPages.pen", { defaultValue: "Pen" })} ${loc.name}`}</p>
          </div>
          {/* ANIMALS IN PEN */}
          <div style={{  ...flexStyle, gridArea: "1/2/2/3", overflow: "hidden" }}>                        
            {loc.animals.length === 0
              // EMPTY PEN
              ? <span style={{ ...flexStyle, ...css.emptyPen, ...fontStyle, opacity: "0.5", height: "30px", paddingLeft: "5px" }}>{t("focus_viewPages.emptyPen", { defaultValue: "Empty Pen" })}</span>
              // WITH ANIMALS
              : <div style={{ ...flexStyle, alignItems: "start", flexDirection: "column" }}>
                  {[...loc.animals].sort((a, b) => a.animal_number - b.animal_number).map((a, index) => (
                    <div key={index} style={{ ...flexStyle, padding: index !== loc.animals.length - 1 ? "0px 0px 5px 5px" : "0px 0px 0px 5px", borderBottom: index !== loc.animals.length - 1 ? "2px solid "+colors.greyTertiary : "none" }}>
                      <img src={icons.animalsColor} style={{ width: "30px" }} alt="Company logo" />
                      <p style={{ ...fontStyle, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "165px" }}>{a.animal_number}</p>
                    </div>
                  ))}
                </div>
              }    
          </div>
        </div>
      ))}
    </div>
  )
})
// #endregion

// #region | Location Buttonlist
const LocationButtonList = memo(({ editMode, onCancel, onSave, onEdit, onBack }: {editMode: boolean, onCancel: () => void, onSave: () => void, onEdit: () => void, onBack: () =>  void}) => {
  const { t } = useTranslation();
  const config = editMode ? [
    {label: t("buttons.save", {defaultValue: "save"}), icon: glyphs.Check({size: "25px"}), action: onSave},
    {label: t("buttons.cancel", {defaultValue: "cancel"}), icon: glyphs.Close({size: "25px"}), action: onCancel},
    {label: t("buttons.edit", {defaultValue: "edit"}), icon: glyphs.Edit({size: "25px"}), action: onEdit, disabled: true},
    {label: t("buttons.goBack", {defaultValue: "go back"}), icon: glyphs.Arrow({size: "25px", rotate: 180}), action: onBack, disabled: true},
  ] : [
    {label: t("buttons.edit", {defaultValue: "edit"}), icon: glyphs.Edit({size: "25px"}), action: onEdit},
    {label: t("buttons.goBack", {defaultValue: "go back"}), icon: glyphs.Arrow({size: "25px", rotate: 180}), action: onBack},
  ]
  
  return createPortal(
    <div style={{ position: "absolute", bottom: "85px", left: "0px", zIndex: "99999" }}>
      <NewButtonList
        contents={config.map((c) => c.label)}
        startEnhancers={config.map((c) => c.icon)}
        onClick={(i) => config[i].action()}
        disabled={config.map((c) => c.disabled ?? false)}
        buttonType={ButtonTypes.tableButton}
        focus_view={true}
        overrides={(i) => ({
          baseStyles: { borderRadius: "0px 10px 10px 0px", height: "70px", width: "70px", justifyContent: "center", gap: "4px", backgroundColor: editMode && i === 2 ? colors.blueLightPrimary + "cc" : colors.blueLightSecondary + "cc", borderStyle: "solid", borderWidth: "2px 2px 2px 0px", borderColor: colors.blueLightSecondary, backdropFilter: "blur(2px)", zIndex: editMode ? "9999999" : "999", filter: editMode && i === 3 ? "brightness(0.7)" :  "1" },
          hoverStyles: {backgroundColor: colors.blueLightSecondary},
          contentStyles: { ...fonts.fontSize3 },
          startEnhancerStyles: { width: "25px", height: "25px", margin: "0px" }
          })}
        listOverrides={{
          listStyle: ListStyle_Types.vertical
        }}
      />
    </div>, document.body)
  
})
// #endregion

export default function FV_Operations() {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [locationToggle, setLocationToggle] = useState<boolean>(false);
  // const [moveAnimalModal, setMoveAnimalModal] = useState<boolean>(false) TODO
  const [system, setSystem] = useState<number>()
  const [section, setSection] = useState<number>()
  const [location, setLocation] = useState<number>()

  const systemOptions = useSystemOptions();
  const { sectionOptions, locationOptions } = useSectionOptions(undefined, system, section);
  const [feedSkipTime, setFeedSkipTime] = useState<Map<number, number>>(new Map());
  const feedSkipSuccess = useRef<{[locationId: number]: boolean}>({})
  const dirtRef = useRef<MaintainAnimal[]>([]);
  
  const { skipFeedTimeLocations} = useSkipFeedTime();

  const dispatch = useAppDispatch()
  
  useEffect(() => {
    dispatch(clearActiveAnimals());
    dispatch(clearActiveLocations());
  },[dispatch, section, system])

  // System select defaults
  useEffect(() => {
    if (systemOptions.length > 0 && !system) {
      setSystem(Number(systemOptions[0].id));
    }
  }, [systemOptions, system]);
    
  // Section select defaults
  useEffect(() => {
    if (sectionOptions.length > 0 && section === undefined) {
      setSection(Number(sectionOptions[0].id));
    }
  }, [system, sectionOptions, section]);

  // #region | Data fetch
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
    dispatch(clearActiveAnimals());
    dispatch(clearActiveLocations());
    return { domain, connection }
  }, [dispatch, farm]);

  const { maintainEntries: updateAnimals } = useAnimal(connection);
  const { call: getLfSectionInfo, data: sectionState } = useSection(connection);

  useEffect(() => {
    getLfSectionInfo({ fetchAmount: 500 })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const locations = useMemo(() => sectionState && sectionState[domain] ? sectionState[domain].data.find(sec => sec.section_id === section)?.section_locations : [], [sectionState, domain, section])
  const animalsAtLocations = useAppSelector((state) => {const locationsId = locations?.map((location) => location.location_id); if (locationsId && locationsId.length) { return selectByLocations(state.graphQlData.animals,  locationsId)}});
  const activeLocations = useAppSelector((state) => selectAllActiveSections(state.activeState.section));
  useEffect(() => {
    if (isEqual(locations, activeLocations)) {
      return;
    }

    dispatch(addActiveLocations(locations ?? []));
    dispatch(addActiveAnimals(locations?.reduce((accumulator, location) => { 
      const animal = location.animals[0];
      if (animal) {
        accumulator.push(animal);
      }
      return accumulator;},[] as AnimalInfo[]) ?? []))
  },[locations, dispatch, activeLocations])
  // #endregion
  
  // #region | Pen Navigation
  const penSelect = useMemo(() => {
    const currentIndex = locationOptions.findIndex((pen) => pen.id === location);

    return (
      <div style={{ gridArea: "1/1/2/2", backgroundColor: colors.greyPrimary, width: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", padding: "2px 0px 7px 0px", borderRadius: "0px" }}>
        <NewButton
          content={t("buttons.prev", { defaultValue: "prev" })}
          buttonType={ButtonTypes.textButton}
          startEnhancer={glyphs.Arrow({ rotate: 180 })}
          onClick={() => { setLocation(Number(locationOptions[(currentIndex - 1 + locationOptions.length) % locationOptions.length].id)) }}
          overrides={{
            contentStyles: { ...fonts.fontSize2 },
            hoverStyles: { opacity: "1" },
            disabledStyles: { opacity: "0.3" }
          }}
          disabled={(location === undefined || (locations && locations.length === 0)) ? true : false}
        />
          <NewSelect
            options={locationOptions}
            onChange={(i) => { setLocation(Number(i?.id))}}
            fieldOverrides={{
              fieldWidth: "100px",
              fieldStyle: NewFieldStyles.greySecondary,
              focus_view: true
            }}
            valueOverrides={{
              defaultValue: (system === undefined&&section === undefined) || locationOptions.length === 0 ? {id: NaN, label: ""} as SelectFieldOption_Values : locationOptions.find(opt => opt.id === location)
            }}
          />
        <NewButton
          content={t("buttons.next", { defaultValue: "next" })}
          buttonType={ButtonTypes.textButton}
          endEnhancer={glyphs.Arrow({})}
          onClick={() => { setLocation(Number(locationOptions[(currentIndex + 1) % locationOptions.length].id)) }}
          overrides={{
            contentStyles: { ...fonts.fontSize2 },
            hoverStyles: { opacity: "1" },
            disabledStyles: { opacity: "0.3" }
          }}
          disabled={(location === undefined || (locations && locations.length === 0)) ? true : false}
        />
      </div>
    )
  }, [location, locationOptions, locations, section, system, t])
  // #endregion

  // #region | Content to display
  const contentToDisplay = useMemo(() => {
    let view: JSX.Element | undefined;
    
    if (!system) {
      view = <p style={{...fonts.paragraph, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>Choose a System</p>
    }
    else if (!section) {
      view = <p style={{...fonts.paragraph, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>Choose a Section</p>
    }
    else if (locations && !location) {
      view = <PenList locations={locations} onClick={(id: number) => setLocation(id)} />
    }
    else if (locations && location) {
      view = <>
        <LocationButtonList
          editMode={editMode}
          onCancel={() => {
            setEditMode(false);
            if (animalsAtLocations) {
              dispatch(resetActiveAnimals(animalsAtLocations));
            }
            if (locations) {
              dispatch(resetActiveLocations(locations))
            }
            dirtRef.current = [];
            setFeedSkipTime(new Map())
          }}
          onBack={() => setLocation(undefined)}
          onEdit={() => {setEditMode(true); feedSkipSuccess.current={}}}
          onSave={async () => {
            if (updateAnimals) {
              updateAnimals({ data: dirtRef.current });
            }

            const groupSkips = new Map<number, number[]>();

            feedSkipTime.entries().forEach(([locationId, time]) => {
              const timeLocations = groupSkips.get(time);
              if (timeLocations) {
                groupSkips.set(time, [...timeLocations, locationId]);
                return;
              } 
              groupSkips.set(time, [locationId]);
            })
           
            for (const [timeOffsetInHours, locationIds] of Array.from(groupSkips.entries())) {
              const time = Math.floor(Date.now()/1000) + 60*60*timeOffsetInHours;
              const skippedResult = await skipFeedTimeLocations({domain: farm, skipFeedTimeLocations: {time, locationIds} } )
              skippedResult.forEach((result) => feedSkipSuccess.current[result.id] = result.success);
            } 
            
            setFeedSkipTime(new Map())

            setEditMode(false);
            dirtRef.current = [];
          }}
        />
        <FvLocationFeeder domain={domain} location={location} editMode={editMode} dirtyDataRef={dirtRef} feedSkipHandler={{feedSkipTime, setFeedSkipTime, feedSkipSuccess}} />
      </>
    }
    return (
      <div style={{ gridArea: "2/1/3/2", display: "grid", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "start", boxSizing: "border-box", overflowY: "auto", height: "100%", scrollbarColor: colors.greyPrimary + colors.transparent, scrollbarWidth: "thin", gridTemplateRows: "352px 1fr", gridTemplateColumns: "1fr" }}>
        {view}
      </div>
    )
  }, [animalsAtLocations, dispatch, domain, editMode, farm, feedSkipTime, location, locations, section, skipFeedTimeLocations, system, updateAnimals])
  // #endregion

  // #region | Location select
  const locationSelect = useMemo(() => { 
    return (
      <div style={{ marginTop: "20px" }}>
        <NewSelectList
          options={[ systemOptions, sectionOptions ]}
          onChanges={[
            (i) => { setSystem(Number(i?.id)); setSection(undefined); setLocation(undefined); feedSkipSuccess.current={} },
            (i) => { setSection(Number(i?.id)); setLocation(undefined); feedSkipSuccess.current={} },
          ]}
          placeholders={["", ""]}
          headers={[ t(`focus_viewPages.system`, { defaultValue: "system" }), t(`focus_viewPages.section`, { defaultValue: "section" }) ]}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyDarkSecondary,
            fieldWidth: "100%",
            focus_view: true
          }}
          listOverrides={{
            listStyle: ListStyle_Types.vertical,
            listGap: "10px",
          }}
          valueOverrides={{
            defaultValues: [
              systemOptions.find(opt => Number(opt.id) === system) ?? { id: "", label: "" },
              sectionOptions.find(opt => Number(opt.id) === section) ?? { id: "", label: "" }
            ]
          }}
        />
      </div>
    )
   }, [section, sectionOptions, system, systemOptions, t]);
    
  
  // #endregion
  
  return ( <>
    <frames.Page_inner_focus_view>
        <PageHeader topHeader={t(`menuHeaders.${Pages.op_operations}`, {defaultValue: "focus view"})}/>
      <frames._page_inner_content>
        <div style={{display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: locationToggle ? "190px 1fr" : "40px 1fr", height: "100%", width: "100%", gap: "10px", boxSizing: "border-box"}}>
          
          {/* ---------- Location ---------- */}
          <div style={{gridArea: "1/1/2/2", backgroundColor: colors.greyDarkPrimary, borderRadius: "10px", padding: "10px 20px", boxSizing: "border-box", height: "100%", overflow: "hidden"}}>
            <div style={{ display: "flex", gap: "10px", cursor: "pointer", WebkitTapHighlightColor: "transparent", }} onClick={() => setLocationToggle(!locationToggle)} >
              <p style={{...fonts.header,...fonts.fontSize2,color: colors.greyLightTertiary, marginBottom: "0px"}}> {t(`focus_viewPages.location`, { defaultValue: "location" })} </p>
              <glyphs.Arrow transparency={locationToggle ? 1 : 0.3} rotate={locationToggle ? 90 : 0} />
            </div>
            {locationToggle ? ( <>{locationSelect}</> ) : ( <></> )}
          </div>

          {/* ---------- Animal ---------- */}
          <div style={{ gridArea: "2/1/3/2", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", boxSizing: "border-box", display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "50px 1fr", overflow: "hidden", height: "100%", zIndex: editMode ? "9999" : undefined }}>
            {penSelect}
            {contentToDisplay}
          </div>

          {editMode ? <Overlay onClickAlert beforeUnload /> : <></>}
        </div>
      </frames._page_inner_content>
    </frames.Page_inner_focus_view>

    
    {/* {moveAnimalModal ?
      <MoveAnimalModal
        isModalOpen={moveAnimalModal}
        onClose={() => setMoveAnimalModal(false)}
        pen={selectedPen}
      />
    : <></>} */}
  </> );
}